import React, { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { useUser, SignInButton } from "@clerk/clerk-react";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  SEO,
} from "../ui";

const AppLayout: React.FC = () => {
  const { isSignedIn } = useUser();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!isSignedIn) {
    return (
      <>
        <SEO
          title="Sign in — WhoBrew"
          description="Sign in to your WhoBrew account."
          noindex
        />
        <Section tone="page" spacing="loose">
          <Container width="prose" className="text-center">
            <Eyebrow>Members only</Eyebrow>
            <Display as="h1" scale="md" italicWord="continue" className="mt-5">
              Sign in to continue.
            </Display>
            <p className="mt-7 text-body text-coffee-700 dark:text-cream-200">
              You need an account to access this page.
            </p>
            <div className="mt-10">
              <SignInButton mode="modal">
                <Button size="lg">Sign in</Button>
              </SignInButton>
            </div>
          </Container>
        </Section>
      </>
    );
  }

  return (
    <>
      <SEO title="Dashboard — WhoBrew" description="Your WhoBrew dashboard." noindex />
      <div className="min-h-screen bg-cream-50 dark:bg-coffee-900">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between bg-coffee-900 dark:bg-cream-100 px-4 py-3 sticky top-0 z-40 border-b border-coffee-800 dark:border-latte-300">
          <button
            onClick={() => setDrawerOpen(true)}
            className="text-cream-50 dark:text-coffee-900 p-2 -ml-1 hover:opacity-70 transition-opacity"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/my-card" className="font-display text-lg text-cream-50 dark:text-coffee-900">
            WhoBrew<span className="text-accent-400">.</span>
          </Link>
          <div className="w-8" />
        </div>

        {/* Mobile drawer overlay */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-coffee-900/60 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute left-0 top-0 bottom-0 w-72">
              <Sidebar onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex">
          {/* Desktop sidebar */}
          <div className="hidden lg:block w-64 fixed inset-y-0 left-0 z-30">
            <Sidebar />
          </div>

          {/* Main content */}
          <main className="flex-1 lg:ml-64 min-h-screen">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-8 lg:py-12">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AppLayout;
